import { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const SPRITES = {
  ship:       [[0,0,1,0,0],[0,1,1,1,0],[1,1,1,1,1],[0,1,0,1,0]],
  invader:    [[0,1,0,0,0,1,0],[0,0,1,0,1,0,0],[0,1,1,1,1,1,0],[1,1,0,1,0,1,1],[1,1,1,1,1,1,1],[0,1,1,1,1,1,0],[0,1,0,0,0,1,0]],
  coin:       [[0,1,1,1,0],[1,1,0,1,1],[1,0,1,0,1],[1,1,0,1,1],[0,1,1,1,0]],
  heart:      [[0,1,1,0,1,1,0],[1,1,1,1,1,1,1],[1,1,1,1,1,1,1],[0,1,1,1,1,1,0],[0,0,1,1,1,0,0],[0,0,0,1,0,0,0]],
  star:       [[0,0,1,0,0],[0,1,1,1,0],[1,1,1,1,1],[0,1,1,1,0],[1,0,0,0,1]],
  mushroom:   [[0,0,1,1,1,0,0],[0,1,1,1,1,1,0],[1,0,1,0,1,0,1],[1,1,1,1,1,1,1],[0,1,0,1,0,1,0],[0,1,1,1,1,1,0]],
  ghost:      [[0,0,1,1,1,0,0],[0,1,1,1,1,1,0],[1,1,0,1,0,1,1],[1,1,1,1,1,1,1],[1,1,1,1,1,1,1],[1,0,1,0,1,0,1]],
  pacman:     [[0,1,1,1,0],[1,1,1,0,0],[1,1,0,0,0],[1,1,1,0,0],[0,1,1,1,0]],
  creeper:    [[0,1,1,1,1,0],[0,1,1,1,1,0],[0,0,1,1,0,0],[0,1,1,1,1,0],[1,1,1,1,1,1],[1,1,0,0,1,1],[1,0,0,0,0,1]],
  potion:     [[0,1,1,0],[1,1,1,1],[1,0,0,1],[1,1,1,1],[0,1,1,0]],
  katana:     [[0,0,0,1,1],[0,0,1,1,0],[0,1,1,0,0],[1,1,0,0,0],[1,0,0,0,0]],
  controller: [[1,1,1,1,1,1,1],[1,0,1,0,1,0,1],[1,1,1,1,1,1,1],[0,1,0,0,0,1,0]],
  diamond:    [[0,0,1,1,0,0],[0,1,1,1,1,0],[1,1,1,1,1,1],[0,1,1,1,1,0],[0,0,1,1,0,0]],
  bullet:     [[0,1,0],[1,1,1],[1,1,1],[0,1,0]],
  flag:       [[1,1,1,0],[1,1,1,0],[1,0,0,0],[1,0,0,0],[1,0,0,0]],
  key:        [[0,1,1,0],[1,0,0,1],[0,1,1,0],[0,0,1,0],[0,1,1,1]],
  crown:      [[1,0,1,0,1],[1,1,1,1,1],[1,1,1,1,1],[0,1,1,1,0]],
};

const BLUE        = '#2563EB';
const RED         = '#DC2626';
const YELLOW      = '#FBBF24';
const SLATE       = '#1E293B';
const DARK_SLATE  = '#0D1520';
const COLORS_LIGHT = ['#2563EB','#DC2626','#FBBF24','#22D3EE','#A855F7','#10B981','#F97316','#EC4899','#F8FAFC'];
const COLORS_DARK  = ['#3B82F6','#1E3A5F','#1D4ED8','#0EA5E9','#6366F1','#0369A1','#1E40AF','#2563EB','#93C5FD'];

const SNIPPETS = [
  'fn update(&mut self) {', 'impl GameState {', 'pub fn spawn() ->',
  'match event {', 'for entity in &world {', 'ctx.draw_sprite();',
  'Enemy::default()', 'if hp <= 0 { die(); }', 'const GRAVITY = 9.8;',
  'Player::jump()', 'world.update(delta);', 'asset.load("hero")',
  'coroutine Start() {', 'void OnCollision() {', 'rb.AddForce(dir);',
  'GetComponent<T>()', 'Instantiate(prefab)', 'SceneManager.Load()',
  'useEffect(() => {', 'useState(0)', 'const dispatch =',
  'shader.setUniform()', 'glDrawArrays(GL_TRIANGLES)',
  'SELECT * FROM saves', 'INSERT INTO scores', 'INNER JOIN players',
  'async fetchLevel(id)', 'await physics.step()', 'emit("levelUp", xp)',
  'fsm.transition("run")', 'quadTree.insert(obj)', 'pathfind(start,end)',
];

const GAME_REFS = [
  { label: 'MARIO',        color: '#DC2626' },
  { label: 'ZELDA',        color: '#FBBF24' },
  { label: 'DOOM',         color: '#DC2626' },
  { label: 'SONIC',        color: '#2563EB' },
  { label: 'TETRIS',       color: '#22D3EE' },
  { label: 'METROID',      color: '#10B981' },
  { label: 'PORTAL',       color: '#F97316' },
  { label: 'CELESTE',      color: '#EC4899' },
  { label: 'HOLLOW KNIGHT',color: '#A855F7' },
  { label: 'STARDEW',      color: '#10B981' },
  { label: 'MINECRAFT',    color: '#10B981' },
  { label: 'UNDERTALE',    color: '#F8FAFC' },
];

function drawSprite(ctx, sprite, x, y, size, color) {
  ctx.fillStyle = SLATE;
  sprite.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (cell) ctx.fillRect(x + c * size + 1, y + r * size + 1, size, size);
    })
  );
  ctx.fillStyle = color;
  sprite.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (cell) ctx.fillRect(x + c * size, y + r * size, size, size);
    })
  );
}

function rand(min, max) { return Math.random() * (max - min) + min; }
function randItem(arr)   { return arr[Math.floor(Math.random() * arr.length)]; }

export default function AnimatedBackground() {
  const canvasRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let raf;
    let t        = 0;
    let tickerX  = 0;

    const COLORS = isDark ? COLORS_DARK : COLORS_LIGHT;
    const DOT_COLOR = isDark ? '#1E3A5F' : SLATE;
    const SCAN_COLOR = isDark ? '#0D1520' : SLATE;
    const TEXT_COLOR = isDark ? '#1E3A5F' : SLATE;
    const TICKER_COLOR = isDark ? '#1E3A5F' : SLATE;
    const CORNER_COLOR = isDark ? '#1E3A5F' : BLUE;

    function resize() {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const spriteKeys = Object.keys(SPRITES);

    const floaters = Array.from({ length: 24 }, () => ({
      type:       randItem(spriteKeys),
      x:          rand(0, canvas.width),
      y:          rand(0, canvas.height),
      vx:         rand(-0.45, 0.45),
      vy:         rand(-0.45, 0.45),
      size:       Math.floor(rand(3, 6)),
      color:      randItem(COLORS),
      pulse:      rand(0, Math.PI * 2),
      pulseSpeed: rand(0.012, 0.035),
      opacity:    rand(0.09, isDark ? 0.15 : 0.2),
    }));

    const shapeColors = isDark
      ? ['#1E3A5F14', '#1D4ED810', '#0369A112', '#0EA5E914', '#6366F114', '#1E40AF12']
      : [BLUE + '14', RED + '10', YELLOW + '12', '#22D3EE14', '#A855F714', '#10B98112'];
    const shapes = Array.from({ length: 8 }, () => ({
      x:     rand(0, canvas.width),
      y:     rand(0, canvas.height),
      w:     rand(50, 160),
      h:     rand(50, 160),
      vx:    rand(-0.2, 0.2),
      vy:    rand(-0.2, 0.2),
      fill:  randItem(shapeColors),
      stroke:randItem(isDark ? ['#1E3A5F', '#1D4ED8', '#0369A1', '#0EA5E9'] : [BLUE, RED, YELLOW, '#22D3EE', '#A855F7', '#10B981']),
      round: Math.random() > 0.5,
    }));

    const shooters = Array.from({ length: 6 }, () => ({
      x:     rand(0, canvas.width),
      y:     rand(0, canvas.height),
      speed: rand(1.5, 3.0),
      angle: rand(0, Math.PI * 2),
      color: randItem(COLORS),
      trail: [],
    }));

    const codeLines = Array.from({ length: 14 }, () => ({
      text:     randItem(SNIPPETS),
      x:        rand(0, canvas.width),
      y:        rand(0, canvas.height),
      speed:    rand(0.2, 0.5),
      opacity:  rand(0.04, 0.08),
      fontSize: Math.floor(rand(8, 13)),
    }));

    const gameLabels = Array.from({ length: 8 }, () => {
      const ref = randItem(GAME_REFS);
      return {
        text:     ref.label,
        color:    ref.color,
        x:        rand(0, canvas.width),
        y:        rand(0, canvas.height),
        speed:    rand(0.15, 0.35),
        opacity:  rand(0.04, 0.07),
        fontSize: Math.floor(rand(7, 11)),
      };
    });

    const TICKER =
      '  MARIO  ◆  ZELDA  ◆  DOOM  ◆  SONIC  ◆  MINECRAFT  ◆  HOLLOW KNIGHT  ◆  ' +
      'CELESTE  ◆  PORTAL  ◆  UNDERTALE  ◆  METROID  ◆  STARDEW  ◆  TETRIS  ◆  ' +
      'GAME DEV  ◆  PIXEL ART  ◆  INSERT COIN  ◆  PRESS START  ◆  ';

    function loop() {
      t += 0.016;
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      // Grid dots
      ctx.globalAlpha = isDark ? 0.12 : 0.05;
      ctx.fillStyle   = DOT_COLOR;
      for (let x = 0; x < W; x += 40)
        for (let y = 0; y < H; y += 40)
          ctx.fillRect(x, y, 1, 1);

      // Shapes
      ctx.globalAlpha = 1;
      for (const s of shapes) {
        s.x += s.vx; s.y += s.vy;
        if (s.x < -s.w) s.x = W + s.w;
        if (s.x > W + s.w) s.x = -s.w;
        if (s.y < -s.h) s.y = H + s.h;
        if (s.y > H + s.h) s.y = -s.h;
        ctx.globalAlpha = 1;
        ctx.fillStyle   = s.fill;
        if (s.round) {
          ctx.beginPath();
          ctx.roundRect(s.x, s.y, s.w, s.h, 8);
          ctx.fill();
        } else {
          ctx.fillRect(s.x, s.y, s.w, s.h);
        }
        ctx.globalAlpha  = 0.1;
        ctx.strokeStyle  = s.stroke;
        ctx.lineWidth    = 1.5;
        ctx.strokeRect(s.x + 4, s.y + 4, s.w, s.h);
      }

      // Scanlines
      ctx.globalAlpha = isDark ? 0.04 : 0.02;
      ctx.fillStyle   = SCAN_COLOR;
      for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);

      // Code lines
      ctx.globalAlpha = 1;
      for (const line of codeLines) {
        line.y += line.speed;
        if (line.y > H + 20) {
          line.y    = -20;
          line.x    = rand(0, W);
          line.text = randItem(SNIPPETS);
        }
        ctx.globalAlpha = line.opacity;
        ctx.fillStyle   = TEXT_COLOR;
        ctx.font        = `${line.fontSize}px 'Roboto Mono', monospace`;
        ctx.fillText(line.text, line.x, line.y);
      }

      // Game name labels
      for (const g of gameLabels) {
        g.y += g.speed;
        if (g.y > H + 20) {
          g.y = -20;
          g.x = rand(0, W);
          const ref = randItem(GAME_REFS);
          g.text  = ref.label;
          g.color = ref.color;
        }
        ctx.globalAlpha = g.opacity;
        ctx.fillStyle   = g.color;
        ctx.font        = `bold ${g.fontSize}px 'Press Start 2P', monospace`;
        ctx.fillText(g.text, g.x, g.y);
      }

      // Floaters
      for (const f of floaters) {
        f.x     += f.vx;
        f.y     += f.vy;
        f.pulse += f.pulseSpeed;
        const m  = 60;
        if (f.x < -m) f.x = W + m;
        if (f.x > W + m) f.x = -m;
        if (f.y < -m) f.y = H + m;
        if (f.y > H + m) f.y = -m;
        ctx.save();
        ctx.globalAlpha = f.opacity + Math.sin(f.pulse) * 0.025;
        const sp = SPRITES[f.type];
        const cx = f.x + (sp[0].length * f.size) / 2;
        const cy = f.y + (sp.length    * f.size) / 2;
        ctx.translate(cx, cy);
        ctx.rotate(Math.sin(f.pulse * 0.3) * 0.08);
        ctx.translate(-cx, -cy);
        drawSprite(ctx, sp, f.x, f.y, f.size, f.color);
        ctx.restore();
      }
      ctx.globalAlpha = 1;

      // Bullet trails
      for (const s of shooters) {
        s.x += Math.cos(s.angle) * s.speed;
        s.y += Math.sin(s.angle) * s.speed;
        s.trail.push({ x: s.x, y: s.y });
        if (s.trail.length > 14) s.trail.shift();
        if (s.x < 0 || s.x > W || s.y < 0 || s.y > H) {
          s.x = rand(0, W); s.y = rand(0, H);
          s.angle = rand(0, Math.PI * 2);
          s.trail = [];
        }
        s.trail.forEach((pt, i) => {
          ctx.globalAlpha = (i / s.trail.length) * 0.3;
          ctx.fillStyle   = s.color;
          const sz = Math.ceil(i / s.trail.length * 4);
          ctx.fillRect(Math.floor(pt.x), Math.floor(pt.y), sz, sz);
        });
        ctx.globalAlpha = 0.45;
        ctx.fillStyle   = s.color;
        ctx.fillRect(Math.floor(s.x), Math.floor(s.y), 4, 4);
      }
      ctx.globalAlpha = 1;

      const corners = [[0, 0], [W - 32, 0], [0, H - 32], [W - 32, H - 32]];
      ctx.globalAlpha  = isDark ? 0.15 : 0.07;
      ctx.fillStyle    = CORNER_COLOR;
      ctx.strokeStyle  = CORNER_COLOR;
      ctx.lineWidth    = 2;
      corners.forEach(([cx, cy]) => {
        ctx.fillRect(cx, cy, 16, 16);
        ctx.strokeRect(cx + 3, cy + 3, 16, 16);
      });

      ctx.globalAlpha = isDark ? 0.08 : 0.055;
      ctx.fillStyle   = TICKER_COLOR;
      ctx.font        = "8px 'Press Start 2P', monospace";
      tickerX -= 0.7;
      const tw = ctx.measureText(TICKER).width;
      if (tickerX < -tw) tickerX = 0;
      ctx.fillText(TICKER + TICKER, tickerX, H - 10);

      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(loop);
    }

    loop();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      'fixed',
        inset:         0,
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        zIndex:        -1,
      }}
    />
  );
}
